import { usePostHog } from 'posthog-js/react';
import useInitUserOnboard from 'hooks/useInitUserOnboard';
import useFirebaseAnalytics, {
  ANALYTICS_EVENTS,
  logEvent,
} from 'shared/analytics';
import { useEffect } from 'react';
import { SplashScreen } from '@capacitor/splash-screen';
import { useInboxMessages } from 'features/inboxMessages/hooks/useInboxMessages';
import useAppInfo from 'hooks/useAppInfo';
import useOnboardingState from 'state/useOnboardingState';
import { useAuthState } from 'state/useAuthState';
import OnboardingWalkMe from 'components/OnboardingWalkMe';
import { useLocation } from 'react-router-dom';
import useSelectedLocationState from 'state/useSelectedLocationState';
import { useIapInitializer } from 'hooks/useIapInitializer';
import { AlertCameraPlayerProvider } from 'features/cameras/hooks/useAlertCameraPlayerState';
import { useSupporterStatus } from 'hooks/useSupporterStatus';
import mapboxgl from 'mapbox-gl';
import { enqueueSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { Button, Link } from '@mui/material';
import Routes from './Routes';

const NotSupportedSnackbarAction = (): JSX.Element => {
  const { t } = useTranslation();
  return (
    <Button
      component={Link}
      size="small"
      variant="watchduty-text-uppercase"
      href="https://support.watchduty.org/hc/en-us/articles/36184204332045"
      target="_blank"
    >
      {t('map.notSupported.learnMore')}
    </Button>
  );
};

const Navigation = (): JSX.Element => {
  const { onboardCompleted } = useOnboardingState();
  const posthog = usePostHog();
  useFirebaseAnalytics();
  useIapInitializer();
  useInitUserOnboard();
  useAppInfo(true);
  useInboxMessages();
  const { updateAuth } = useAuthState();
  const location = useLocation();
  const { setSelectedLocation } = useSelectedLocationState();
  const { t } = useTranslation();

  useEffect(() => {
    if (!mapboxgl.supported()) {
      posthog.capture(ANALYTICS_EVENTS.SUPPORT.MAPBOX_NOT_SUPPORTED);
      logEvent({
        name: ANALYTICS_EVENTS.SUPPORT.MAPBOX_NOT_SUPPORTED,
      });
      enqueueSnackbar(t('map.notSupported.message'), {
        variant: 'error',
        persist: true,
        action: NotSupportedSnackbarAction,
      });
    }
  }, [t, posthog]);

  useEffect(() => {
    // If we've completed the onboarding once - we can immediately hide the splash screen
    // otherwise, let the the logic in useInitUserOnboard handle it
    const hideSplashScreen = async (): Promise<void> => {
      await SplashScreen.hide();
    };
    if (onboardCompleted) {
      hideSplashScreen();
    }
  }, [onboardCompleted]);

  const supporterStatus = useSupporterStatus();

  useEffect(() => {
    if (supporterStatus) {
      updateAuth({
        hasActiveMembership: supporterStatus.hasActiveMembership,
        user: { supporterStatus },
      });
    }
  }, [supporterStatus, updateAuth]);

  useEffect(() => {
    setSelectedLocation(null);
  }, [location, setSelectedLocation]);

  useEffect(() => {
    // explicitly handle $pageview events for posthog
    if (posthog) {
      posthog.capture('$pageview', {
        $current_url: window.location.pathname,
      });
    }
  }, [location, posthog]);

  return (
    <>
      <AlertCameraPlayerProvider>
        <Routes />
      </AlertCameraPlayerProvider>
      <OnboardingWalkMe />
    </>
  );
};

export default Navigation;
